import { Injectable } from '@angular/core';
import { Notification } from '@models/notification.model';
import { firstValueFrom, from, map, Observable, take } from 'rxjs';
import { getMessaging, getToken } from '@angular/fire/messaging';
import { HttpClient } from '@angular/common/http';
import { getApp } from '@angular/fire/app';
import { environment } from '@environments/environment';
import { UtilityService } from './utility.service';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Store } from '@ngrx/store';
import { SessionStoreSelectors, UserStoreActions, UserStoreSelectors } from 'src/app/root-store';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private isNative = Capacitor.isNativePlatform();

    constructor(
        private http: HttpClient,
        private utilityService: UtilityService,
        private store: Store
    ) {}

    async promptToAcceptNotifications(message: string, acceptFn: Function): Promise<void> {
        let fn = acceptFn;

        if (this.isNative) {
            const nativeAcceptFn = await this.getNativeAcceptFunction(acceptFn);
            if (!nativeAcceptFn) {
                return; // User already accepted notifications and we have a token
            }

            fn = nativeAcceptFn;
        } else if (Notification.permission !== 'default') {
            return;
        }

        const header = 'Accept Notifications';
        const acceptBtn = 'Accept';
        const denyBtn = 'Deny';
        this.utilityService.prompt(header, message, acceptBtn, denyBtn, fn).pipe(
            take(1)
        ).subscribe();
    }

    pushNotification(notification: Notification): Observable<void> {
        const url = `${environment.apiBaseUrl}/communication/push-notification`;
        return this.http.post<void>(url, notification);
    }

    requestPermission(): Observable<string> {
        if (this.isNative) {
            // PushNotifications.register() does not return a token, so let's return an empty string
            // and handle the token retrieval in the getNativeAcceptFunction
            return from(PushNotifications.register()).pipe(map(() => ''));
        }

        const app = getApp();
        const messaging = getMessaging(app);
        return from(getToken(messaging));
    }

    private async getNativeAcceptFunction(acceptFn: Function): Promise<Function | null> {
        // Since we cannot get the permission status in native without prompting (if is the first time),
        // let's decide whether to prompt or not based on the presence of the notification token in the user record
        const id = await firstValueFrom(this.store.select(SessionStoreSelectors.getUserId));
        const token = await firstValueFrom(this.store.select(UserStoreSelectors.getUserNativeNotificationToken(id)));
        if (token) {
            return null; // User already accepted notifications and we have a token
        }

        const fn = async () => {
            const permission = await PushNotifications.requestPermissions();
            if (permission.receive === 'granted') {
                const { token: nativeNotificationToken } = await FirebaseMessaging.getToken();
                const properties = { nativeNotificationToken };
                this.store.dispatch(UserStoreActions.updateUserPropertiesRequest({ id, properties }));
                acceptFn();
            }
        };
        return fn;
    }
}
